<template>
  <page-controls>
    <template v-slot:left>
        <div class="w-full grow @3xl/controls:max-w-md">
            <vx-filterable :active="active" @toggle="toggleFilterable">
                <template v-slot:placeholder>
                <span class="mr-2">Find Campaigns</span>
                <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                    {{ pluralized('filter', filtersCount, true )}}
                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                </vx-badge>
                </template>
                <template v-slot:form>
                <form @submit.prevent="apply" class="space-y-4">
                    <div class="space-y-1">
                    <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                        Keyword Search
                    </filter-label>

                    <vx-input
                        type="text"
                        data-test="campaign-controls-query"
                        placeholder="Search"
                        maxlength="180"
                        size="lg"
                        v-model="filters.query">
                        <template v-slot:right>
                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                        </template>
                    </vx-input>
                    </div>

                    <div v-if="isCorporate" class="space-y-1">
                        <filter-label :canReset="!!filters.duration_types?.length" @reset="filters.duration_types = []">
                            Duration Type
                        </filter-label>

                        <vx-selectable multiple placeholder="Select type" :options="typesOptions"
                            v-model="filters.duration_types"
                            size="lg"
                            :reduce="(option) => option.value">
                        </vx-selectable>
                    </div>

                    <div class="space-y-1">
                        <filter-label :canReset="!!filters.type" @reset="filters.type = undefined">
                            Audience Type
                        </filter-label>

                        <vx-selectable placeholder="Select type" :options="audienceTypesOptions"
                            v-model="filters.type"
                            size="lg"
                            :clearable="false"
                            :reduce="(option) => option.value">
                        </vx-selectable>
                    </div>

                    <div class="space-y-1">
                        <filter-label :canReset="!!filters.ui_statuses?.length" @reset="filters.ui_statuses = []">
                            Status
                        </filter-label>
                        <vx-selectable multiple placeholder="Select status" :options="statusesOptions"
                            v-model="filters.ui_statuses"
                            size="lg"
                            :reduce="(option) => option.value">
                        </vx-selectable>
                    </div>

                    <div>
                        <filter-label :canReset="!!selectedDate" @reset="updateDates(undefined)">
                            Date Created
                        </filter-label>
                        <vx-selectable placeholder="Select date" :options="datesRangeOptions" :modelValue="selectedDate"
                            :reduce="(option) => option.value" @update:modelValue="updateDates" size="lg" :clearable="false">
                        </vx-selectable>
                    </div>

                    <div >
                        <filter-label :canReset="!!filters.category?.length" @reset="filters.category = undefined">
                            Category
                        </filter-label>
                        <category-filter-select
                            v-model="filters.category"
                            size="lg"
                        >
                        </category-filter-select>
                    </div>


                    <div v-if="isCorporate && FEATURES.time_zone_aware_campaigns">
                        <filter-label :canReset="filters.time_zone_aware !== undefined" @reset="filters.time_zone_aware = undefined">
                            Time-Zone-Aware Start
                        </filter-label>
                        <vx-selectable placeholder="Filter by time-zone-aware start" :options="timeZoneAwareOptions" v-model="filters.time_zone_aware"
                            :reduce="(option) => option.value" size="lg" :clearable="false">
                        </vx-selectable>
                    </div>

                    <in-flow-filterable-item v-if="isCorporate && FEATURES.flows" v-model="filters.flow">
                    </in-flow-filterable-item>

                    <div class="flex gap-2 pt-4">
                        <vx-button type="button" size="lg" color="muted-light" @click="cancel">Cancel</vx-button>
                        <vx-button data-test="campaign-controls-apply" size="lg" type="submit" block>
                            Apply
                            <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                        </vx-button>
                    </div>
                </form>
                </template>
            </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
            <vx-select v-model="filters.sort_field"
                @update:modelValue="apply()" :options="[
                {
                    value: 'name',
                    label: 'Sort by: Name',
                },
                {
                    value: 'id',
                    label: 'Sort by: Created',
                },
                ]"></vx-select>
            <vx-button
                @click.prevent="
                    filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                    apply();
                "
                type="button" color="white" size="lg" shadow
                data-test="sort-direction"
                :aria-label="`Sort ${filters.sort_dir === 'asc' ? 'ascending' : 'descending'}. Press to toggle sort direction.`">
                <font-awesome-icon widthAuto
                :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"></font-awesome-icon>
            </vx-button>
        </div>
    </template>
    <template v-slot:right>
        <router-button v-if="isCorporate" data-test="new-campaign" :to="{ name: 'campaigns.new' }" class="flex justify-between" size="lg">
            New Campaign <font-awesome-icon widthAuto :icon="faCirclePlus" />
        </router-button>
    </template>
  </page-controls>
</template>

<script setup>
import { computed, ref, watch, inject, nextTick } from 'vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { VxBadge, VxButton, VxFilterable, VxInput, VxSelect, VxSelectable } from '@voxie/frontend-components';
import { faCircleCheck, faMagnifyingGlass, faCirclePlus, faCircleXmark, faArrowDownWideShort, faArrowUpWideShort } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getClientTz } from '../../../utils/date';
import InFlowFilterableItem from '~/components/automation/flows/components/InFlowFilterableItem.vue';
import CategoryFilterSelect from '~/components/general/categories/CategoryFilterSelect.vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, audienceTypes } from '~/constants/campaign';
import { pluralized } from '~/components/filters';
import { filtersKey } from '~/composables/useCursorPagination';
import RouterButton from '~/components/general/RouterButton.vue';

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    modelValue: {
        type: Object,
        required: true,
    },
});

const isCorporate = computed(() => !props.groupId && !props.orgUnitId);

const FEATURES = inject('FEATURES');

const typesOptions = [
    {
        value: 'one_time',
        label: 'Blast',
    },
    {
        value: 'evergreen',
        label: 'Evergreen',
    }
];

const audienceTypesOptions = audienceTypes().map((audienceType) => {
    return {
        value: audienceType.name,
        label: audienceType.label,
    }
}).filter((audienceType) => {
    return isCorporate.value || [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(audienceType.value);
});

const statusesOptions = [
  {
    label: 'Draft',
    value: 'draft',
  },
  {
    label: 'Scheduled',
    value: 'scheduled',
  },
  {
    label: 'Live',
    value: 'live',
  },
  {
    label: 'Paused',
    value: 'paused',
  },
  {
    label: 'Complete',
    value: 'complete',
  },
];
const datesRangeOptions = [
  {
    label: 'Last 7 days',
    value: '7',
  },
  {
    label: 'Last 14 days',
    value: '14',
  },
  {
    label: 'Last 21 days',
    value: '21',
  },
  {
    label: 'Last 30 days',
    value: '30',
  },
];

const timeZoneAwareOptions = [
  {
    label: 'True',
    value: 1,
  },
  {
    label: 'False',
    value: 0,
  },
];


const filters = ref({});

watch(() => filtersKey(props.modelValue), () => {
    filters.value = {...props.modelValue};
}, {
    immediate: true,
});

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const selectedDate = ref();
const active = ref(false);

watch(
  () => props.modelValue.start_date,
  startDateValue => {
    if (startDateValue === undefined) {
      selectedDate.value = undefined;
    }
  }
);

const toggleFilterable = (event) => {
  active.value = event;
}

const isFlowFilterApplied = computed(() => {
    if (!props.modelValue.flow) {
        return false;
    }

    return props.modelValue.flow.in_flow !== undefined || !!(props.modelValue.flow.flow_id)
});
const filtersCount = computed(() => {
    let count = 0;

    count += props.modelValue.duration_types?.length ? 1 : 0;
    count += props.modelValue.ui_statuses?.length ? 1 : 0;
    count += props.modelValue.start_date ? 1 : 0;
    count += props.modelValue.query?.length ? 1 : 0;
    count += isFlowFilterApplied.value ? 1 : 0;
    count += (props.modelValue.time_zone_aware === undefined ? 0 : 1);
    count += props.modelValue.category?.length ? 1 : 0;
    count += props.modelValue.type ? 1 : 0;

    return count;
});

const updateDates = async (days) => {
  selectedDate.value = days;
  filters.value.start_date = days ? toUTCDateTimeFormat(dayjs().tz(getClientTz()).utc().subtract(days, 'days')) : undefined;
  await nextTick();
  filters.value.end_date = days ? toUTCDateTimeFormat(dayjs().tz(getClientTz())) : undefined;
}

const reset = () => {
  active.value = false;
  emit('reset');
  selectedDate.value = undefined;
}

const cancel = async () => {
    filters.value = { ...props.modelValue }
    active.value = false;
}

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
}

</script>
